import { Box } from "@chakra-ui/react";
import { FeedPagesViewer } from "./FeedPagesViewer";
import { FeedComicInfoCard } from "./FeedComicInfoCard";
import { FeedRssCluster } from "./FeedRssCluster";
import { FeedCompactComicCard } from "./FeedCompactComicCard";
import { FeedShortVideoCard } from "./FeedShortVideoCard";
import { FeedLongVideoCard } from "./FeedLongVideoCard";
import { FeedTopCommentCluster } from "./FeedTopCommentCluster";
import { FeedStreakCard } from "./FeedStreakCard";
import { FeedFeaturedCreatorCard } from "./FeedFeaturedCreatorCard";
import { FeedCreatorHighlightCluster } from "./FeedCreatorHighlightCluster";
import { FeedBundleCard } from "./FeedBundleCard";
import { FeedCardActions } from "./FeedCardActions";
import { useFeedReleaseData } from "./hooks/useFeedReleaseData";
import { FeedItem } from "./types";

interface FeedSlotProps {
  item: FeedItem;
  isActive: boolean;
  onAdvanceFeed: () => void;
  onRetreatFeed: () => void;
  hasFeedPrev: boolean;
  hasFeedNext: boolean;
}

/**
 * One feed slot — comic-release deck or RSS section card depending on item type.
 *
 * Unknown types (sent by a newer backend the FE hasn't been updated for) are
 * silently skipped so a backend-ahead-of-frontend deploy doesn't crash the feed.
 */
export function FeedSlot(props: FeedSlotProps) {
  if (props.item.type === "rss_section") {
    return (
      <Box w="100%" h="100%" position="relative">
        <FeedRssCluster item={props.item} isActive={props.isActive} />
      </Box>
    );
  }
  if (props.item.type === "comic_release") {
    return <ComicReleaseSlot {...props} item={props.item} />;
  }
  if (
    props.item.type === "comic_recommendation" ||
    props.item.type === "genre_dive"
  ) {
    return (
      <Box w="100%" h="100%" position="relative">
        <FeedCompactComicCard item={props.item} isActive={props.isActive} />
      </Box>
    );
  }
  if (props.item.type === "short_video") {
    return (
      <Box w="100%" h="100%" bg="black" position="relative">
        <Box
          w="100%"
          h="100%"
          position="relative"
          bg="black"
          borderRadius="3xl"
          overflow="hidden"
          boxShadow="0 12px 40px rgba(0,0,0,0.55), 0 0 0 1px rgba(255,255,255,0.04)"
        >
          <FeedShortVideoCard item={props.item} isActive={props.isActive} />
        </Box>
      </Box>
    );
  }
  if (props.item.type === "long_video") {
    return (
      <Box w="100%" h="100%" bg="black" position="relative">
        <Box
          w="100%"
          h="100%"
          position="relative"
          bg="rgba(20, 18, 30, 0.92)"
          borderRadius="2xl"
          overflow="hidden"
          border="1px solid rgba(255,255,255,0.05)"
        >
          <FeedLongVideoCard item={props.item} isActive={props.isActive} />
        </Box>
      </Box>
    );
  }
  if (props.item.type === "top_comment") {
    return (
      <Box w="100%" h="100%" position="relative">
        <FeedTopCommentCluster item={props.item} isActive={props.isActive} />
      </Box>
    );
  }
  if (props.item.type === "streak_milestone") {
    return (
      <Box w="100%" h="100%" bg="black" overflow="hidden" position="relative">
        <FeedStreakCard item={props.item} isActive={props.isActive} />
      </Box>
    );
  }
  if (props.item.type === "featured_creator") {
    return (
      <Box w="100%" h="100%" position="relative">
        <FeedFeaturedCreatorCard item={props.item} isActive={props.isActive} />
      </Box>
    );
  }
  if (props.item.type === "creator_highlight") {
    return (
      <Box w="100%" h="100%" position="relative">
        <FeedCreatorHighlightCluster item={props.item} isActive={props.isActive} />
      </Box>
    );
  }
  if (props.item.type === "bundle") {
    return (
      <Box w="100%" h="100%" position="relative">
        <FeedBundleCard item={props.item} isActive={props.isActive} />
      </Box>
    );
  }
  return null;
}

function ComicReleaseSlot({
  item,
  isActive,
  onAdvanceFeed,
  onRetreatFeed,
  hasFeedPrev,
  hasFeedNext,
}: FeedSlotProps & { item: Extract<FeedItem, { type: "comic_release" }> }) {
  const { release, pages, isLoading, isPaywalled } = useFeedReleaseData(
    item.release_uuid,
    true,
  );

  return (
    // Outer slot fills its parent (parent already has horizontal gutter).
    <Box w="100%" h="100%" bg="black" position="relative">
      <Box
        w="100%"
        h="100%"
        position="relative"
        bg="black"
        borderRadius="3xl"
        overflow="hidden"
        boxShadow="0 16px 50px rgba(0,0,0,0.65)"
        border="2px solid rgba(255,255,255,0.14)"
      >
        <FeedPagesViewer
          releaseUuid={item.release_uuid}
          pages={pages}
          isPaywalled={isPaywalled}
          isLoading={isLoading}
          isInteractive={isActive}
          readingDirection={item.reading_direction}
          onAdvanceFeed={onAdvanceFeed}
          onRetreatFeed={onRetreatFeed}
          hasFeedPrev={hasFeedPrev}
          hasFeedNext={hasFeedNext}
          release={release}
        />
        {/* Title overlay always rendered so it doesn't pop in on activation;
            gradient backing keeps it readable over busy artwork. The actions
            slot lives on the right edge of the title block so the pills
            don't overlap the comic name/description. */}
        <FeedComicInfoCard
          comic={item.comic}
          release={release}
          actions={<FeedCardActions isActive={isActive} />}
        />
      </Box>
    </Box>
  );
}
